<script setup lang="ts">
import WidgetContainer from "@/components/EditView/widgets/WidgetContainer.vue";
import {WidgetProps} from "@/utils/presenters/interfaces.ts";
import EditScreenFieldLayout from "@/utils/constants/EditScreenFieldLayout.ts";
import ErrorTooltip from "@/components/EditView/ErrorTooltip.vue";
import {ref, useTemplateRef} from "vue";
import type {QInput} from "quasar";
import {focusAndSelectInput, type NamaNavigateToNextEmit} from "@/utils/common/NamaVueWidget.ts";
import BarcodeTextFieldWidgetUtil from "@/utils/editView/widgets/BarcodeTextFieldWidgetUtil.ts";

const props = defineProps<WidgetProps>();
const emit = defineEmits<NamaNavigateToNextEmit>()
const fieldId = props.field.id!;
const fieldUtil = new BarcodeTextFieldWidgetUtil(props.presenter, props.fieldMetaData);
const qInputRef = useTemplateRef<QInput>("qInputRef")
const isSpannedLayout = props.field.layout == EditScreenFieldLayout.spanned
const keepFocusIn = ref(false);

function focus() {
  focusAndSelectInput(qInputRef);
}

function blur() {
  if (!qInputRef.value)
    return;
  qInputRef.value.blur();
}

function onFocus() {
  keepFocusIn.value = true;
  fieldUtil.onFocus();
}

function onBlur() {
  if (keepFocusIn.value)
    focus();
  else
    fieldUtil.onBlur();
}

function canReceiveFocus() {
  return !fieldUtil.isFieldDisabled(props.forceEnableField);
}

async function onKeydown(e: KeyboardEvent) {
  if (e.key == 'F4' || e.key == 'Escape') {
    keepFocusIn.value = false;
    blur()
  }
  if (!(e.key == "Enter" || e.key == "Tab"))
    return;
  if (e.ctrlKey || e.altKey)
    return fieldUtil.insertNewLine(qInputRef);
  if (e.key == "Enter" || e.key == "Tab") {
    if (!fieldUtil.inputFieldValueRef.value.trim()) {
      keepFocusIn.value = false;
      blur();
      emit("navigateToNext", {keyboardEvent: e});
      return;
    }
    await fieldUtil.insertBarcode(qInputRef);
  }
  e.preventDefault();
}

setTimeout(() => {
  if (keepFocusIn.value)
    focus();
}, 100)

defineExpose({focus, canReceiveFocus})
</script>
<template>
  <WidgetContainer
    :field="field"
    :entityType="presenter.entityType"
    :isSpanned="isSpanned" :isFieldDisabled="fieldUtil.isFieldDisabled(forceEnableField)"
    :isFieldRequired="fieldUtil.isFieldRequired()" :doNotApplyThemeLayouts="doNotApplyThemeLayouts"
    :isInsideComposite="isInsideComposite">
    <template v-slot:widget>
      <q-input outlined dense hide-bottom-space
               :readonly="fieldUtil.isFieldDisabled(forceEnableField)"
               :id="field.id" ref="qInputRef"
               type="textarea"
               autocomplete="off"
               :error="!!props.presenter.fetchFieldErrorMsg(fieldId)"
               class="control-big-text-microphone-opacity tw-overflow-hidden"
               :class="[keepFocusIn ? 'nama-barcode-input-focused':'nama-focused-input',
               fieldUtil.isFieldDisabled(forceEnableField)?'nama-field-input-disabled':(keepFocusIn ? 'nama-barcode-input':'nama-field-input'),
               {'nama-customize-dense-input':!isSpannedLayout}]"
               :input-class="[isSpannedLayout?'nama-input-class tw-h-16':'tw-font-normal tw-text-base dark:tw-text-white focus:tw-ring-transparent focus:tw-ring-0',
               keepFocusIn ? 'tw-bg-green-400':'tw-bg-transparent', 'tw-px-2']"
               :model-value="fieldUtil.inputFieldValueRef.value" @update:model-value="fieldUtil.onPartialUserInput" @focus="onFocus"
               @blur="onBlur"
               @keydown="onKeydown">
        <ErrorTooltip v-if="presenter.fetchFieldErrorMsg(fieldId)" :error-msg="presenter.fetchFieldErrorMsg(fieldId)"/>
      </q-input>
    </template>
  </WidgetContainer>
</template>
